import { forwardRef, useState, type InputHTMLAttributes } from 'react';
import { CalendarIcon } from '../../../icons/calendar';
import { cn } from '../../../lib/utils';
import { Calendar } from '../calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../popover';

export interface DatePickerProps extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'value' | 'onChange'
> {
  /** ISO date `YYYY-MM-DD`. Empty string when not set. */
  value: string;
  onChange: (value: string) => void;
  /** Inline error styling — border + ring turn destructive. */
  hasError?: boolean;
  /** Disables typing + opening the calendar. */
  disabled?: boolean;
  /** Latest allowed date (inclusive). */
  maxDate?: Date;
  /** Earliest allowed date (inclusive). */
  minDate?: Date;
  /** Year + month dropdowns in the calendar caption (default `true`). */
  captionDropdown?: boolean;
}

/** Parse `YYYY-MM-DD` as a local date (avoids UTC shift that drops to the previous day). */
function parseIsoDateLocal(iso: string): Date | undefined {
  if (!iso) return undefined;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return undefined;
  const year = Number(m[1]);
  const month = Number(m[2]);
  const day = Number(m[3]);
  if (!year || !month || !day) return undefined;
  return new Date(year, month - 1, day);
}

function formatIsoDate(date: Date): string {
  const y = date.getFullYear();
  const mo = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${mo}-${d}`;
}

/**
 * DatePicker — o melhor dos dois: o campo é um `<input type="date">` NATIVO
 * (digitação segmentada/travada `dd/mm/aaaa`, clicar no dia/mês/ano seleciona o
 * segmento) e o ÍCONE abre o calendário CUSTOM (com switch de ano/mês). Ambos
 * commitam `value` como ISO `YYYY-MM-DD`. O indicador de calendário nativo fica
 * escondido — quem abre o calendário é o nosso botão.
 */
export const DatePicker = forwardRef<HTMLInputElement, DatePickerProps>(
  (
    {
      value,
      onChange,
      hasError = false,
      disabled = false,
      maxDate,
      minDate,
      captionDropdown = true,
      className,
      ...rest
    },
    ref
  ) => {
    const [open, setOpen] = useState(false);
    const selected = parseIsoDateLocal(value);

    // Com o switch de ano/mês (padrão), o dropdown de ano precisa de um intervalo.
    const now = new Date();
    const startMonth =
      minDate ?? (captionDropdown ? new Date(now.getFullYear() - 100, 0, 1) : undefined);
    const endMonth =
      maxDate ?? (captionDropdown ? new Date(now.getFullYear() + 10, 11, 31) : undefined);

    return (
      <div
        data-slot="date-picker"
        className={cn(
          'flex h-9 w-full items-center rounded-md border bg-background shadow-2xs transition-colors',
          hasError
            ? 'border-destructive focus-within:outline focus-within:outline-2 focus-within:outline-offset-1 focus-within:outline-destructive'
            : 'border-input focus-within:outline focus-within:outline-2 focus-within:outline-offset-1 focus-within:outline-primary',
          disabled && 'cursor-not-allowed opacity-50',
          className
        )}
      >
        <input
          ref={ref}
          type="date"
          value={value}
          disabled={disabled}
          min={minDate ? formatIsoDate(minDate) : undefined}
          max={maxDate ? formatIsoDate(maxDate) : undefined}
          onChange={(e) => onChange(e.target.value)}
          className={cn(
            'min-w-0 flex-1 bg-transparent px-3 text-sm text-foreground outline-none',
            'disabled:cursor-not-allowed',
            // Esconde o ícone/picker nativo — usamos nosso próprio botão de calendário.
            '[&::-webkit-calendar-picker-indicator]:hidden [&::-webkit-inner-spin-button]:hidden'
          )}
          {...rest}
        />
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              disabled={disabled}
              aria-label="Abrir calendário"
              className="mr-1 flex size-7 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground disabled:pointer-events-none"
            >
              <CalendarIcon className="size-4" />
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              mode="single"
              selected={selected}
              defaultMonth={selected}
              captionLayout={captionDropdown ? 'dropdown' : 'label'}
              startMonth={startMonth}
              endMonth={endMonth}
              onSelect={(date) => {
                if (date) {
                  onChange(formatIsoDate(date));
                  setOpen(false);
                }
              }}
              disabled={[
                ...(minDate ? [{ before: minDate }] : []),
                ...(maxDate ? [{ after: maxDate }] : []),
              ]}
            />
          </PopoverContent>
        </Popover>
      </div>
    );
  }
);

DatePicker.displayName = 'DatePicker';
